import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { EquipmentTypeBadge } from "@/components/EquipmentTypeBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, ChevronLeft, ChevronRight, FileText } from "lucide-react";

const TABS = [
    { id: "submitted", label: "Pending review" },
    { id: "approved", label: "Approved" },
    { id: "rejected", label: "Rejected" },
    { id: "all", label: "All" },
];

const STATUS_PILL = {
    submitted: "bg-amber-50 text-amber-900 ring-amber-200",
    approved: "bg-emerald-50 text-emerald-900 ring-emerald-200",
    rejected: "bg-red-50 text-red-700 ring-red-200",
    draft: "bg-slate-100 text-slate-600 ring-slate-200",
    equipment_assigned: "bg-blue-50 text-blue-900 ring-blue-200",
    cancelled: "bg-slate-100 text-slate-500 ring-slate-200",
};

const STATUS_LABEL = {
    submitted: "submitted",
    approved: "approved",
    rejected: "rejected",
    draft: "draft",
    equipment_assigned: "equipment assigned",
    cancelled: "cancelled",
};

function fmt(s) {
    if (!s) return "—";
    try {
        return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
        return s;
    }
}

const PAGE_SIZE = 25;

export default function AdminSubmissionsListPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const status = searchParams.get("status") || "submitted";
    const q = searchParams.get("q") || "";
    const skip = Number(searchParams.get("skip") || 0);
    // Phase AE — opt-in chip to include ``equipment_assigned`` rows in
    // the ``All`` / ``Approved`` views (excluded by default).
    const includeAssigned = searchParams.get("include_assigned") === "true";
    // Phase CANCEL-DRAFT (Part B / FE) — opt-in chip to include
    // soft-deleted 'cancelled' rows (draft cancellations) in the list.
    const includeCancelled = searchParams.get("include_cancelled") === "1";

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState(q);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { status_filter: status, q, limit: PAGE_SIZE, skip };
            if (includeAssigned) params.include_assigned = true;
            if (includeCancelled) params.include_cancelled = 1;
            const { data } = await api.get("/admin/submissions", { params });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [status, q, skip, includeAssigned, includeCancelled]);

    useEffect(() => {
        load();
    }, [load]);

    const setParam = (k, v) => {
        const next = new URLSearchParams(searchParams);
        if (v == null || v === "") next.delete(k);
        else next.set(k, String(v));
        if (k !== "skip") next.delete("skip");
        setSearchParams(next, { replace: true });
    };

    const onSearchSubmit = (e) => {
        e.preventDefault();
        setParam("q", searchInput.trim());
    };

    const page = Math.floor(skip / PAGE_SIZE) + 1;
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <DashboardShell>
            <div className="fade-up">
                <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-3">
                    Operations · Submissions
                </p>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                    Customer submissions
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-600 leading-relaxed">
                    Review applications, approve, reject, or reopen for customer edits.
                </p>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="inline-flex flex-wrap gap-1 rounded-md border border-slate-200 p-1 bg-white" role="tablist">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            data-testid={`submissions-tab-${t.id}`}
                            onClick={() => setParam("status", t.id)}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                status === t.id ? "bg-blue-900 text-white" : "text-slate-600 hover:bg-slate-50"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                <form onSubmit={onSearchSubmit} className="flex gap-2 items-center" data-testid="submissions-search">
                    {/* Phase AE — opt-in chip to surface submissions that
                        have already been past the assign-equipment step.
                        Off by default per Option A filter semantics. */}
                    {(status === "all" || status === "approved") && (
                        <label
                            data-testid="submissions-include-assigned-chip"
                            className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium cursor-pointer transition-colors ${
                                includeAssigned
                                    ? "border-blue-300 bg-blue-50 text-blue-900"
                                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                            }`}
                        >
                            <input
                                type="checkbox"
                                checked={includeAssigned}
                                onChange={(e) =>
                                    setParam("include_assigned", e.target.checked ? "true" : "")
                                }
                                data-testid="submissions-include-assigned-input"
                                className="h-3.5 w-3.5"
                            />
                            Include equipment-assigned
                        </label>
                    )}
                    {/* Phase CANCEL-DRAFT (Part B / FE) — "Cancelled" filter chip.
                        Shown on the "All" tab; hidden by default. */}
                    {status === "all" && (
                        <label
                            data-testid="submissions-include-cancelled-chip"
                            aria-pressed={includeCancelled ? "true" : "false"}
                            className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium cursor-pointer transition-colors ${
                                includeCancelled
                                    ? "border-red-300 bg-red-50 text-red-800"
                                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                            }`}
                        >
                            <input
                                type="checkbox"
                                checked={includeCancelled}
                                onChange={(e) =>
                                    setParam("include_cancelled", e.target.checked ? "1" : "")
                                }
                                data-testid="submissions-include-cancelled-input"
                                className="h-3.5 w-3.5"
                            />
                            Cancelled
                        </label>
                    )}
                    <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            data-testid="submissions-search-input"
                            placeholder="Search by company or customer…"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="pl-8 w-64"
                        />
                    </div>
                    <Button type="submit" variant="outline" data-testid="submissions-search-submit">Search</Button>
                </form>
            </div>

            <Card className="mt-5">
                <CardContent className="p-0">
                    <Table data-testid="submissions-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Company</TableHead>
                                <TableHead>Customer</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Submitted</TableHead>
                                <TableHead>Reviewed</TableHead>
                                <TableHead className="text-right">Action</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center text-sm text-slate-500 py-10">
                                        Loading…
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center text-sm text-slate-500 py-12">
                                        <FileText className="mx-auto mb-3 text-slate-300" size={28} />
                                        No submissions match this filter yet.
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && items.map((s) => (
                                <TableRow key={s.id} data-testid={`submission-row-${s.id}`}
                                    className={s.status === "cancelled" ? "opacity-70" : ""}>
                                    <TableCell className="font-medium text-slate-900">
                                        {s.company_name || <span className="text-slate-400">—</span>}
                                        <div className="mt-1">
                                            <EquipmentTypeBadge data={s} testId={`submission-equipment-${s.id}`} />
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="text-sm text-slate-900">{s.customer_name || "—"}</div>
                                        <div className="text-xs text-slate-500">{s.customer_email || "—"}</div>
                                    </TableCell>
                                    <TableCell>
                                        <span
                                            data-testid={`submission-status-${s.id}`}
                                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ring-1 ring-inset ${STATUS_PILL[s.status] || "bg-slate-100 text-slate-700 ring-slate-200"}`}
                                        >
                                            {STATUS_LABEL[s.status] || s.status}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-sm text-slate-600">{fmt(s.submitted_at)}</TableCell>
                                    <TableCell className="text-sm text-slate-600">
                                        {s.reviewed_at ? (
                                            <>
                                                {fmt(s.reviewed_at)}
                                                {s.reviewer_name && (
                                                    <div className="text-xs text-slate-500">{s.reviewer_name}</div>
                                                )}
                                            </>
                                        ) : "—"}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            asChild
                                            size="sm"
                                            variant="outline"
                                            data-testid={`view-submission-${s.id}`}
                                        >
                                            <Link to={`/admin/submissions/${s.id}`}>View</Link>
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {total > PAGE_SIZE && (
                <div className="mt-4 flex items-center justify-between text-sm text-slate-600" data-testid="submissions-pagination">
                    <span>
                        Page {page} of {pages} · {total} total
                    </span>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={skip === 0}
                            onClick={() => setParam("skip", Math.max(0, skip - PAGE_SIZE))}
                            data-testid="submissions-prev-page"
                        >
                            <ChevronLeft size={14} /> Prev
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={skip + PAGE_SIZE >= total}
                            onClick={() => setParam("skip", skip + PAGE_SIZE)}
                            data-testid="submissions-next-page"
                        >
                            Next <ChevronRight size={14} />
                        </Button>
                    </div>
                </div>
            )}
        </DashboardShell>
    );
}
